import {
  Box,
  CircularProgress,
  Divider,
  HStack,
  Icon,
  Tag,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  ArrowPathIcon,
  CalendarDaysIcon,
  LockClosedIcon,
  NoSymbolIcon,
} from "@heroicons/react/24/outline";
import { useUpdateEffect, useWindowsFocus } from "@reactuses/core";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  Stripe_SubscriptionStatus,
  useSubscriptionsQuery,
} from "../../graphql/generated/user-hooks";
import { formatPrice } from "../../helpers/format-price";
import { getSubscriptionBadge } from "../../helpers/subscription-status";
import { Button } from "../../components/chackra-ui-button";

type Props = {
  onCancelSubscription: (subscription: {
    id: string;
    currentPeriodEnd: Date;
  }) => void;
  onReactivateSubscription: (subscription: {
    id: string;
    currentPeriodEnd: Date;
  }) => void;
};

export const SubscriptionSection = (props: Props) => {
  const focused = useWindowsFocus();
  const { i18n, t } = useTranslation();
  const currentLocale = i18n.language;
  const toast = useToast();
  const { data, loading, refetch } = useSubscriptionsQuery({
    fetchPolicy: "network-only",
    context: { endpoint: "fitnext" },
    onError: (error) => {
      toast({
        title: error.message,
        status: "error",
        isClosable: true,
      });
    },
  });

  useUpdateEffect(() => {
    if (focused) {
      refetch();
    }
  }, [focused]);

  return (
    <Box w="full">
      <Text
        fontSize="20px"
        fontWeight={600}
        fontFamily="'Barlow Semi Condensed', sans-serif"
        color="#13192E"
        mb={3}
        px={1}
      >
        {t("subscriptions.title")}
      </Text>
      <Box
        bg="white"
        borderWidth="1px"
        borderColor="slate.100"
        borderRadius="12px"
        p={5}
      >
      <VStack w="full" spacing={5} align="start">
        {loading || !data ? (
          <HStack>
            <CircularProgress isIndeterminate color="blue.500" size={4} />
            <Text>{t("subscriptions.loading")}</Text>
          </HStack>
        ) : data.subscriptions.edges.length > 0 ? (
          <VStack w="full" spacing={5}>
            {data.subscriptions.edges.map(({ node }, index) => {
              const amount = node.items.reduce(
                (acc, curr) => acc + (curr.price.unitAmount ?? 0),
                0
              );

              const isPackage = node.items.some(
                (i) => i.price.metadata.type === "package"
              );

              const isPastDue =
                node.status === Stripe_SubscriptionStatus.PastDue;
              const isCanceling = !isPastDue && !!node.cancelAt;

              const adjustedCancelDate = node.cancelAt
                ? dayjs(node.cancelAt).subtract(1, "day")
                : null;

              const formattedCancelDate = adjustedCancelDate
                ?.locale(currentLocale)
                .format("DD/MM/YYYY");

              const badge = getSubscriptionBadge({
                t,
                status: node.status,
                cancelAt: node.cancelAt,
                isPackage,
                formattedCancelDate: formattedCancelDate ?? "",
              });

              const interval = node.items[0]?.price.recurring;

              return (
                <VStack key={node.id} w="full" align="start" spacing={0}>
                  {/* ── Header: badge + name (left) · price (right) ── */}
                  <HStack w="full" justify="space-between" align="flex-start">
                    <VStack align="start" spacing={2}>
                      <Tag colorScheme={badge.colorScheme}>{badge.label}</Tag>
                      <Text
                        fontFamily="'Barlow Semi Condensed', sans-serif"
                        fontSize="18px"
                        fontWeight={600}
                        lineHeight="1.3"
                      >
                        {node.items
                          .map(
                            (i) =>
                              i.price.product.metadata[
                                `locale_${currentLocale}`
                              ] ?? i.price.product.name
                          )
                          .join(", ")}
                      </Text>
                    </VStack>

                    <VStack align="end" spacing={0} flexShrink={0}>
                      <Text
                        fontFamily="'Barlow Condensed', sans-serif"
                        fontSize="24px"
                        fontWeight={700}
                        lineHeight="1.1"
                      >
                        {formatPrice({ t, amount })}
                      </Text>
                      {!isPackage && interval && (
                        <Text
                          fontSize="14px"
                          fontWeight={500}
                          color="slate.500"
                        >
                          /{" "}
                          {t(
                            `subscriptions.${interval.interval.toLowerCase()}`
                          )}
                        </Text>
                      )}
                    </VStack>
                  </HStack>

                  {/* ── Divider ── */}
                  <Divider borderColor="slate.200" my={4} />

                  {/* ── Meta rows ── */}
                  <VStack w="full" spacing={2} align="start">
                    {isPastDue ? (
                      <MetaRow
                        icon={LockClosedIcon}
                        color="red.500"
                        text={t("subscriptions.status.pastDue")}
                      />
                    ) : isPackage || isCanceling ? (
                      <>
                        <MetaRow
                          icon={NoSymbolIcon}
                          text={t("subscriptions.noAutomaticRenewal")}
                        />
                        {adjustedCancelDate && (
                          <MetaRow
                            icon={CalendarDaysIcon}
                            text={`${t("subscriptions.accessUntil")} ${adjustedCancelDate.locale(currentLocale).format("DD/MM/YYYY")}`}
                          />
                        )}
                      </>
                    ) : (
                      <>
                        <MetaRow
                          icon={ArrowPathIcon}
                          text={t("subscriptions.recurringMembership")}
                        />
                        {node.upcomingInvoice && (
                          <MetaRow
                            icon={CalendarDaysIcon}
                            text={`${t("subscriptions.nextBilling")} ${dayjs(node.upcomingInvoice.createdAt).locale(currentLocale).format("DD/MM/YYYY")}`}
                          />
                        )}
                      </>
                    )}
                  </VStack>

                  {/* ── Action button ── */}
                  {!isPackage && (
                    <Box w="full" mt={4}>
                      {isPastDue ? null : isCanceling ? (
                        <Button
                          variant="senseiFilled"
                          w="full"
                          onClick={() =>
                            props.onReactivateSubscription({
                              id: node.id,
                              currentPeriodEnd: node.currentPeriodEnd,
                            })
                          }
                        >
                          {t("subscriptions.reactivateSubscription")}
                        </Button>
                      ) : (
                        <Button
                          variant="senseiOutlined"
                          w="full"
                          onClick={() =>
                            props.onCancelSubscription({
                              id: node.id,
                              currentPeriodEnd: node.currentPeriodEnd,
                            })
                          }
                        >
                          {t("subscriptions.cancelSubscription")}
                        </Button>
                      )}
                    </Box>
                  )}

                  {/* ── Separator between multiple subscriptions ── */}
                  {index < data.subscriptions.edges.length - 1 && (
                    <Divider borderColor="slate.200" mt={5} />
                  )}
                </VStack>
              );
            })}
          </VStack>
        ) : (
          <Text>{t("subscriptions.noSubscriptions")}</Text>
        )}
      </VStack>
      </Box>
    </Box>
  );
};

const MetaRow = (props: {
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  text: string;
  color?: string;
}) => (
  <HStack spacing={2} align="center">
    <Icon boxSize={4} color={props.color ?? "slate.500"}>
      <props.icon />
    </Icon>
    <Text fontSize="14px" color={props.color ?? "slate.600"}>
      {props.text}
    </Text>
  </HStack>
);
